import { useState, useEffect } from 'react';
import api from '../../services/api';
import { Calendar, Camera, Upload, TrendingUp, FileText, X } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useDropzone } from 'react-dropzone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/mileage`;

const MonthlyMileage = () => {
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [monthlyData, setMonthlyData] = useState(null);
  const [startMileage, setStartMileage] = useState('');
  const [endMileage, setEndMileage] = useState('');
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allMonthlyRecords, setAllMonthlyRecords] = useState([]);

  useEffect(() => {
    fetchMonthlyData();
    fetchMonthTrips();
    fetchAllMonthlyRecords();
  }, [selectedMonth]);

  // Auto-save when start mileage changes
  useEffect(() => {
    if (startMileage && parseFloat(startMileage) > 0) {
      const timer = setTimeout(() => {
        handleAutoSave();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [startMileage]);

  const fetchAllMonthlyRecords = async () => {
    try {
      const response = await api.get(`${API}/monthly-mileage`);
      setAllMonthlyRecords(response.data);
    } catch (error) {
      console.error('Error fetching all monthly records:', error);
    }
  };

  const fetchMonthlyData = async () => {
    try {
      const response = await api.get(`${API}/monthly-mileage/${selectedMonth}`);
      setMonthlyData(response.data);
      setStartMileage(response.data.start_mileage);
      setEndMileage(response.data.end_mileage);
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching monthly data:', error);
      }
      setMonthlyData(null);
      setStartMileage('');
      setEndMileage('');
    }
  };

  const fetchMonthTrips = async () => {
    try {
      const [year, month] = selectedMonth.split('-');
      const response = await api.get(`${API}/trips?month=${month}&year=${year}`);
      setTrips(response.data);
    } catch (error) {
      console.error('Error fetching trips:', error);
    }
  };

  const calculateEstimatedEndMileage = () => {
    if (!startMileage || parseFloat(startMileage) === 0) return 0;
    
    // Calculate average from previous months
    if (allMonthlyRecords.length > 0) {
      const validRecords = allMonthlyRecords.filter(
        (record) => record.total_miles > 0 && record.month !== selectedMonth
      );
      
      if (validRecords.length > 0) {
        const avgMiles = validRecords.reduce((sum, record) => sum + record.total_miles, 0) / validRecords.length;
        return parseFloat(startMileage) + avgMiles;
      }
    }
    
    // Default estimate: start + 400 miles
    return parseFloat(startMileage) + 400;
  };

  const handleAutoSave = async () => {
    if (!startMileage || parseFloat(startMileage) === 0) return;

    try {
      // Only save end mileage if it's actually provided and different from start
      const payload = {
        month: selectedMonth,
        start_mileage: parseFloat(startMileage),
        end_mileage: (endMileage && parseFloat(endMileage) > 0 && parseFloat(endMileage) !== parseFloat(startMileage))
          ? parseFloat(endMileage)
          : parseFloat(startMileage) // Save start as placeholder
      };
      
      await api.post(`${API}/monthly-mileage`, payload);
      
      // Don't refetch to avoid loop
      fetchAllMonthlyRecords();
    } catch (error) {
      console.error('Error auto-saving:', error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!startMileage) {
      toast.error('Please enter start mileage');
      return;
    }

    if (endMileage && parseFloat(endMileage) < parseFloat(startMileage)) {
      toast.error('End mileage must be greater than start mileage');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        month: selectedMonth,
        start_mileage: parseFloat(startMileage),
        end_mileage: (endMileage && parseFloat(endMileage) > 0 && parseFloat(endMileage) !== parseFloat(startMileage))
          ? parseFloat(endMileage)
          : parseFloat(startMileage) // Save start as placeholder
      };
        
      await api.post(`${API}/monthly-mileage`, payload);
      toast.success('Monthly mileage saved successfully');
      fetchMonthlyData();
      fetchAllMonthlyRecords();
    } catch (error) {
      console.error('Error saving monthly mileage:', error);
      toast.error('Failed to save monthly mileage');
    } finally {
      setLoading(false);
    }
  };

  const businessMiles = trips.reduce((sum, trip) => sum + trip.distance_miles, 0);
  
  // Determine which end mileage to use
  let displayEndMileage = null;
  let isEndMileageEstimate = false;
  
  // Check if we have actual end mileage (different from start)
  if (endMileage && parseFloat(endMileage) > 0 && parseFloat(endMileage) !== parseFloat(startMileage)) {
    displayEndMileage = parseFloat(endMileage);
  } else if (startMileage && parseFloat(startMileage) > 0) {
    // Show estimate
    displayEndMileage = calculateEstimatedEndMileage();
    isEndMileageEstimate = true;
  }
  
  const totalMiles = displayEndMileage && startMileage 
    ? displayEndMileage - parseFloat(startMileage) 
    : 0;
    
  const personalMiles = totalMiles - businessMiles;

  return (
    <div className="space-y-4 md:space-y-6 fade-in pb-24">
      {/* Month Selector */}
      <div className="glass-card p-4 md:p-6">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4">
          <Calendar className="w-5 h-5 md:w-6 md:h-6 text-[#14B8A6]" />
          <div className="flex-1 w-full">
            <label className="block text-xs md:text-sm font-medium mb-2">Select Month</label>
            <input
              type="month"
              data-testid="month-selector"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="glass-input w-full text-sm md:text-base"
            />
          </div>
        </div>
      </div>

      {/* Mileage Input */}
      <form onSubmit={handleSave} className="glass-card p-4 md:p-6 space-y-4 md:space-y-6">
        <h2 className="text-xl md:text-2xl font-bold">Mileage Management</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div>
            <label className="block text-xs md:text-sm font-medium mb-2">Start of Month Mileage *</label>
            <input
              type="number"
              data-testid="start-mileage-input"
              step="0.1"
              value={startMileage}
              onChange={(e) => setStartMileage(e.target.value)}
              className="glass-input w-full text-sm md:text-base"
              placeholder="Enter start mileage"
              required
            />
          </div>
          <div>
            <label className="block text-xs md:text-sm font-medium mb-2">
              End of Month Mileage 
              <span className="text-gray-500 ml-1">(Optional)</span>
            </label>
            <input
              type="number"
              data-testid="end-mileage-input"
              step="0.1"
              value={endMileage}
              onChange={(e) => setEndMileage(e.target.value)}
              className="glass-input w-full text-sm md:text-base"
              placeholder="Enter end mileage or upload photo"
            />
          </div>
        </div>

        <button
          type="submit"
          data-testid="save-monthly-button"
          disabled={loading}
          className="btn-primary w-full text-sm md:text-base"
        >
          {loading ? 'Saving...' : 'Save Monthly Mileage'}
        </button>
        <p className="text-xs text-gray-500 text-center">
          Start mileage auto-saves. If end mileage is not entered, an estimate will be shown.
        </p>
      </form>

      {/* Summary Cards */}
      {startMileage && parseFloat(startMileage) > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
          <div
            data-testid="total-miles-card"
            className="glass-card p-4 md:p-6"
            style={{ borderColor: 'rgba(139, 92, 246, 0.4)' }}
          >
            <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
              <div className="p-2 md:p-3 rounded-lg" style={{ background: 'rgba(139, 92, 246, 0.2)' }}>
                <TrendingUp className="w-4 h-4 md:w-6 md:h-6 text-[#8B5CF6]" />
              </div>
              <p className="text-xs md:text-sm text-gray-400">Total Miles</p>
            </div>
            <p 
              className={`text-3xl md:text-4xl font-bold ${isEndMileageEstimate ? 'text-gray-500' : 'text-[#8B5CF6]'}`}
            >
              {totalMiles.toFixed(1)}
            </p>
            {isEndMileageEstimate && (
              <p className="text-xs text-gray-600 mt-1">Estimated</p>
            )}
          </div>

          <div
            data-testid="business-miles-card"
            className="glass-card p-4 md:p-6"
            style={{ borderColor: 'rgba(20, 184, 166, 0.4)' }}
          >
            <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
              <div className="p-2 md:p-3 rounded-lg" style={{ background: 'rgba(20, 184, 166, 0.2)' }}>
                <FileText className="w-4 h-4 md:w-6 md:h-6 text-[#14B8A6]" />
              </div>
              <p className="text-xs md:text-sm text-gray-400">Business Miles</p>
            </div>
            <p className="text-3xl md:text-4xl font-bold text-[#14B8A6]">
              {businessMiles.toFixed(1)}
            </p>
            <p className="text-xs md:text-sm text-gray-500 mt-2">
              {trips.length} trip{trips.length !== 1 ? 's' : ''}
            </p>
          </div>

          <div
            data-testid="personal-miles-card"
            className="glass-card p-4 md:p-6"
            style={{ borderColor: 'rgba(6, 182, 212, 0.4)' }}
          >
            <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
              <div className="p-2 md:p-3 rounded-lg" style={{ background: 'rgba(6, 182, 212, 0.2)' }}>
                <Calendar className="w-4 h-4 md:w-6 md:h-6 text-[#06B6D4]" />
              </div>
              <p className="text-xs md:text-sm text-gray-400">Personal Miles</p>
            </div>
            <p 
              className={`text-3xl md:text-4xl font-bold ${isEndMileageEstimate ? 'text-gray-500' : 'text-[#06B6D4]'}`}
            >
              {personalMiles.toFixed(1)}
            </p>
            {isEndMileageEstimate && (
              <p className="text-xs text-gray-600 mt-1">Estimated</p>
            )}
            <p className="text-xs md:text-sm text-gray-500 mt-2">
              {totalMiles > 0 ? `${((personalMiles / totalMiles) * 100).toFixed(1)}%` : '0%'}
            </p>
          </div>
        </div>
      )}

      {/* Business Trips for Month */}
      {trips.length > 0 && (
        <div className="glass-card p-4 md:p-6">
          <h2 className="text-xl md:text-2xl font-bold mb-4">Business Trips This Month</h2>
          <div className="space-y-2 md:space-y-3">
            {trips.map((trip) => (
              <div
                key={trip.id}
                data-testid={`month-trip-${trip.id}`}
                className="bg-[rgba(30,30,30,0.6)] p-3 md:p-4 rounded-lg border border-[rgba(20,184,166,0.2)]"
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs md:text-sm text-[#14B8A6] mb-1 md:mb-2">{trip.date}</p>
                    <p className="text-xs md:text-sm text-gray-300 truncate">
                      {trip.origin.name} → {trip.destination.name}
                    </p>
                  </div>
                  <p className="text-base md:text-xl font-bold text-[#14B8A6] flex-shrink-0">
                    {trip.distance_miles.toFixed(1)} mi
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyMileage;
